import { Theme } from '@emotion/react';
import styled from '@emotion/styled';
import { Link as RouterLink } from 'react-router-dom';

import { typographyVariantStyles } from '../../typography-variants';
import type { LinkSize } from './types';

const sizeStyles: Record<LinkSize, object> = {
  large: { padding: '11px 24px', borderRadius: 8, ...typographyVariantStyles.body1 },
  regular: { padding: '9px 16px', borderRadius: 8, ...typographyVariantStyles.body1 },
  small: { padding: '8px 12px', borderRadius: 8, ...typographyVariantStyles.body2 },
  mini: { padding: '4px 8px', borderRadius: 4, ...typographyVariantStyles.body2 },
};

const linkBaseStyles = ($size: LinkSize) => (theme: Theme) => {
  const size = sizeStyles[$size];

  return {
    // Layout
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,

    // Size + Typography
    ...size,
    fontWeight: 500,

    // Appearance
    color: theme.colors.text.primary,
    textDecoration: 'none',

    // Behaviour
    cursor: 'pointer',
    outline: 'none',
    WebkitTapHighlightColor: 'transparent',
    boxSizing: 'border-box' as const,

    // Transitions
    transition: 'color 0.2s ease',

    '&:hover, &:active': {
      color: theme.colors.text.brand,
      textDecoration: 'underline',
    },

    '&:focus-visible': {
      color: theme.colors.text.brand,
      outline: 'none',
      boxShadow: `0 0 0 3px ${theme.colors.border.focus}`,
    },
  };
};

export const StyledAnchor = styled('a', {
  shouldForwardProp: (prop) => prop !== '$size',
})<{ $size: LinkSize }>(({ theme, $size }) => linkBaseStyles($size)(theme));

export const StyledRouterLink = styled(RouterLink, {
  shouldForwardProp: (prop) => prop !== '$size',
})<{ $size: LinkSize }>(({ theme, $size }) => linkBaseStyles($size)(theme));
